import React from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { AuditLogItem } from '../../../../interfaces/auditLog.interface';
import { AUDIT_LOG_EVENT, AUDIT_LOG_EVENT_VARIANT } from '../../../../utils/appConstant';
import ChangesGrid from './ChangesGrid';
import RequestDetails from './RequestDetails';
import { initialsOf, showDetail, showValue, splitDateTime } from './detailHelpers';

export { initialsOf, showDetail, showValue, splitDateTime };

const BASE = 'auditLogs.list.pageTexts.detail';
const THIRD = 'col-12 col-sm-4';

interface DetailModalContentProps {
  item: AuditLogItem | null;
}

/**
 * Summary card (who, what, event), a Date / Time / Entity row, the field changes, the extra details, and the request
 * details. The solid theme badges and the theme's own green and red text are under 4.5:1, so the event badge and the old
 * and new values use the project's colour pairs (style.scss), and the other coloured text uses the heading colour. The
 * initials sit in an inner element of their own because bg-label-primary forces its own text colour (about 3.4:1) on the
 * element it is on, and the theme's *-subtle backgrounds are not themed for dark mode.
 */
const DetailModalContent: React.FC<DetailModalContentProps> = ({ item }) => {
  const { t } = useTranslation();
  if (!item) return null;

  // A created entry has no old values and a deleted entry has no new values, so only the side that exists is shown
  const showOld = item.event !== AUDIT_LOG_EVENT.created;
  const showNew = item.event !== AUDIT_LOG_EVENT.deleted;
  const extraKeys = Object.keys(item.extra);
  const { date, time } = splitDateTime(item.created_at);

  const field = (label: string, value: React.ReactNode) => (
    <div className={THIRD} key={label}>
      <div className="text-heading text-uppercase small">{label}</div>
      <div className="fw-semibold text-break">{value}</div>
    </div>
  );

  const heading = (icon: string, text: string, count?: number) => (
    <h6 className="fw-bold mb-2 d-flex align-items-center">
      <i className={clsx(icon, 'ri-18px me-2')} aria-hidden="true"></i>
      {text}
      {count !== undefined && (
        <span className="badge rounded-pill bg-label-secondary text-heading ms-2" data-testid="audit-log-changes-count">{count}</span>
      )}
    </h6>
  );

  return (
    <div>
      <div className="d-flex align-items-center gap-3 border rounded p-3 mb-3 audit-panel-bg" data-testid="audit-log-summary">
        <div className="avatar avatar-md flex-shrink-0">
          <span className="avatar-initial rounded-circle bg-label-primary">
            <span className="text-heading fw-semibold">{initialsOf(item.causer_name)}</span>
          </span>
        </div>
        <div className="flex-grow-1 text-break">
          <div className="fw-semibold text-heading">
            {item.causer_name}{item.causer_role ? ` (${item.causer_role})` : ''}
          </div>
          <div>{item.description}</div>
        </div>
        <span className={clsx('badge rounded-pill flex-shrink-0', `audit-event-${AUDIT_LOG_EVENT_VARIANT[item.event] ?? 'secondary'}`)}>{item.event}</span>
      </div>

      <div className="row g-3 mb-3">
        {field(t(`${BASE}.date`), date)}
        {field(t(`${BASE}.time`), time)}
        {field(t(`${BASE}.entity`), showValue(item.subject))}
      </div>

      {item.changes.length > 0 && (
        <div className="mb-3" data-testid="audit-log-changes">
          {heading('ri-history-line', t(`${BASE}.sectionChanges`), item.changes.length)}
          <ChangesGrid changes={item.changes} showOld={showOld} showNew={showNew} />
        </div>
      )}

      {extraKeys.length > 0 && (
        <div className="mb-3" data-testid="audit-log-extra">
          {heading('ri-file-list-3-line', t(`${BASE}.extra`))}
          <div className="border rounded p-3">
            <div className="row g-3">
              {extraKeys.map((key) => field(key, showDetail(item.extra[key])))}
            </div>
          </div>
        </div>
      )}

      <RequestDetails item={item} />
    </div>
  );
};

export default DetailModalContent;
